import { Component, h, Element, Prop, Build } from '@stencil/core';
import { getConstant } from '../../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

/**
 * Social authentication component for SignNow (Google Sign up button)
 *
 * @product signnow
 * @source social-auth
 * @version 1.0.0
 * @category authentication
 * @constant SOCIAL_AUTH_BUTTON_TEXT - "Sign up with Google" - Social auth button text
 */

// Inline Google logo SVG for LCP optimization (eliminates CDN request)
const GoogleIcon = () => (
  <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" class="google-button__logo" aria-hidden="true">
    <path
      class="google-icon"
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M26.0001 13.2955C26.0001 12.3737 25.9157 11.4873 25.7589 10.6364H13.2654V15.6651H20.4046C20.0971 17.2901 19.1625 18.6669 17.7575 19.5887V22.8505H22.0446C24.553 20.5873 26.0001 17.2546 26.0001 13.2955Z"
      fill="#4285F4"
    />
    <path
      class="google-icon"
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M13.2654 26C16.8471 26 19.8498 24.836 22.0446 22.8505L17.7575 19.5887C16.5697 20.3687 15.0502 20.8295 13.2654 20.8295C9.81043 20.8295 6.88603 18.5427 5.8429 15.47H1.41107V18.8381C3.59382 23.0868 8.07991 26 13.2654 26Z"
      fill="#34A853"
    />
    <path
      class="google-icon"
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M5.8429 15.47C5.57759 14.69 5.42684 13.8569 5.42684 13C5.42684 12.1432 5.57758 11.3101 5.84289 10.5301V7.16188H1.41107C0.512645 8.91687 0.00012207 10.9023 0.00012207 13C0.00012207 15.0978 0.512651 17.0831 1.41107 18.8381L5.8429 15.47Z"
      fill="#FBBC05"
    />
    <path
      class="google-icon"
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M13.2654 5.17045C15.213 5.17045 16.9616 5.82636 18.3364 7.11454L22.1411 3.3859C19.8438 1.28818 16.841 0 13.2654 0C8.07991 0 3.59382 2.91324 1.41107 7.16188L5.84289 10.5301C6.88603 7.45733 9.81043 5.17045 13.2654 5.17045Z"
      fill="#EA4335"
    />
    <path
      class="load-icon"
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M18.2602 7.73989C15.3552 4.83481 10.6451 4.83481 7.74001 7.73989L3.84021 3.84009C8.89909 -1.21879 17.1012 -1.21879 22.16 3.84009L18.2602 7.73989Z"
      fill="#EA4335"
    />
    <path
      class="load-icon"
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M7.74001 7.73989C4.83493 10.645 4.83493 15.355 7.74001 18.2601L3.84021 22.1599C-1.21867 17.101 -1.21867 8.89897 3.84021 3.84009L7.74001 7.73989Z"
      fill="#FBBC05"
    />
    <path
      class="load-icon"
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M7.73988 18.2601C10.645 21.1652 15.355 21.1652 18.2601 18.2601L22.1599 22.1599C17.101 27.2188 8.89896 27.2188 3.84008 22.1599L7.73988 18.2601Z"
      fill="#34A853"
    />
  </svg>
);

// Environment constants
const PRODUCTION_DOMAIN = '.signnow.com';
const DEVELOPMENT_DOMAIN = '.signnow-dev.xyz';

// Helper to detect production environment based on hostname
const isProduction = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname.includes('signnow.com') || hostname.includes('mrkhub.com');
};

// Helper to detect mobile devices (desktop only component)
const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 960;
};

// Helper functions
const getAuthUrl = (pathSuffix = '') => {
  const baseUrl = isProduction() ? `https://app${PRODUCTION_DOMAIN}` : `https://app3${DEVELOPMENT_DOMAIN}`;
  return `${baseUrl}${pathSuffix}`;
};

const getDomain = () => (isProduction() ? PRODUCTION_DOMAIN : DEVELOPMENT_DOMAIN);

const getCookie = (name: string) => {
  const prefix = `${name}=`;
  let begin = document.cookie.indexOf(`; ${prefix}`);
  let end = null;

  if (begin === -1) {
    begin = document.cookie.indexOf(prefix);
    if (begin !== 0) return null;
  } else {
    begin += 2;
    end = document.cookie.indexOf(';', begin);
    if (end === -1) end = document.cookie.length;
  }

  return decodeURI(document.cookie.substring(begin + prefix.length, end));
};

const setCookie = (name: string, value: string, options: { expires?: number; domain?: string; path?: string } = {}) => {
  let cookieString = `${name}=${value}`;

  if (options.expires) {
    const date = new Date();
    date.setTime(date.getTime() + options.expires * 24 * 60 * 60 * 1000);
    cookieString += `; expires=${date.toUTCString()}`;
  }

  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }

  if (options.path) {
    cookieString += `; path=${options.path}`;
  }

  document.cookie = cookieString;
};

const removeCookie = (name: string, options: { domain?: string; path?: string } = {}) => {
  setCookie(name, '', { ...options, expires: -1 });
};

const generateXSRFToken = () => {
  const domain = getDomain();
  const currentDate = new Date().getTime();
  const string = currentDate.toString();
  const { length } = string;

  let i = 0;
  let state = 0;

  if (length > 0) {
    while (i < length) {
      state = ((state << 5) - state + string.charCodeAt(i++)) | 0;
    }
    state = Math.abs(state);
  }

  const tokenValue = state.toString();

  setCookie('SN_SOCIAL_AUTH_XSRF_TOKEN', tokenValue, {
    expires: 30,
    domain,
    path: '/',
  });

  return tokenValue;
};

const getProviderFromButton = (button: HTMLElement) => {
  const pathDomain = getDomain();

  removeCookie('XSRF-TOKEN', { domain: pathDomain, path: '/' });

  const spanText = button.querySelector('span')?.textContent;

  if (spanText === 'Facebook') {
    console.log('[Analytics] Facebook Start');
    return 'facebook';
  } else if (spanText === 'Google') {
    console.log('[Analytics] Google Start');
    return 'google';
  } else if (spanText === 'Microsoft') {
    console.log('[Analytics] Microsoft Start');
    return 'microsoft';
  }

  return 'google'; // Default to Google
};

@Component({
  tag: 'sn-social-auth-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNSocialAuthType1 {
  @Element() el!: HTMLElement;

  /**
   * Component constants object containing all required text constants
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  private buttonElement?: HTMLElement;
  private boundHandleClick?: EventListener;
  private boundHandlePageShow?: EventListener;

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
  }

  componentDidLoad() {
    if (!Build.isBrowser) return;

    // Desktop only - skip on mobile devices
    if (isMobile()) return;

    this.buttonElement = this.el.querySelector('.js-sign-button') as HTMLElement;

    this.boundHandleClick = this.handleButtonClick.bind(this);
    if (this.buttonElement) {
      this.buttonElement.addEventListener('click', this.boundHandleClick);
    }

    // When navigating back from Google OAuth, the browser may restore the page
    // from bfcache with the 'is-hidden' class still applied — remove it.
    this.boundHandlePageShow = (e: Event) => {
      if ((e as PageTransitionEvent).persisted) {
        this.el.querySelector('.js-sign-button')?.classList.remove('is-hidden');
      }
    };
    window.addEventListener('pageshow', this.boundHandlePageShow);
  }

  disconnectedCallback() {
    if (!Build.isBrowser) return;

    if (this.buttonElement && this.boundHandleClick) {
      this.buttonElement.removeEventListener('click', this.boundHandleClick);
    }
    if (this.boundHandlePageShow) {
      window.removeEventListener('pageshow', this.boundHandlePageShow);
    }
  }

  private async handleButtonClick(e: Event) {
    e.preventDefault();

    const button = e.currentTarget as HTMLElement;

    // Set social auth origin cookie
    if (!getCookie('social_auth_origin')) {
      setCookie('social_auth_origin', 'landing', {
        domain: getDomain(),
        path: '/',
      });
    }

    const path = getAuthUrl('/auth/v1');
    const provider = getProviderFromButton(button);
    const state = getCookie('SN_SOCIAL_AUTH_XSRF_TOKEN') || generateXSRFToken();

    try {
      const response = await fetch(`${path}/socialite/${provider}/link?state=${state}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Hide Google button and redirect
      this.hideGoogleButton();
      if (Build.isBrowser) {
        window.location.href = data.link;
      }
    } catch (error) {
      console.error('Social auth error:', error);
    }
  }

  private hideGoogleButton() {
    if (!Build.isBrowser) return;

    const googleButton = this.el.querySelector('.js-sign-button');
    if (googleButton) {
      googleButton.classList.add('is-hidden');
    }
  }

  render() {
    // Skip SSR - requires browser APIs for authentication
    if (!Build.isBrowser) {
      return null;
    }

    // Desktop only - don't render on mobile devices
    if (isMobile()) {
      return null;
    }

    return (
      <div class="google-button js-sign-button">
        <GoogleIcon />
        <div class="google-button__text">{getConstant(this.constants, 'SOCIAL_AUTH_BUTTON_TEXT', 'Sign up with Google')}</div>
      </div>
    );
  }
}
